import { isCurrentSession } from './sessionPolicy';
import queryClient from '@Shared/Query/queryClient';
import { PRODUCER_MENU, PRODUCER_ACTION_PERMISSIONS } from './producerMenu';
import { jwtDecode } from '@Shared/Utils/helpers';
import { accountTypeOf, roleNames } from './accountTypes';
import { clearLocalStorageExceptSimulationHistory } from '@Shared/Utils/simulationHistory';

// Las funcionalidades utilizan los permisos del usuario autenticado
// dentro del ámbito de datos del productor seleccionado.
// La administración global siempre utiliza el JWT del usuario autenticado.
export const SCOPED_BUSINESS_PERMISSIONS = new Set([
  ...PRODUCER_MENU.map(item => item.permission), ...PRODUCER_ACTION_PERMISSIONS,
  'producers.delegates.read', 'producers.delegates.manage',
  'portfolio.read', 'portfolio.export',
  'debtors.read', 'debtors.export', 'payroll.read', 'payroll.manage'
]);

const userAuth = {
  storage: sessionStorage,
  getStorage() {
    this.storage = (!sessionStorage.length && localStorage.length) ? localStorage : sessionStorage;
    return this.storage;
  },
  setStorage(remember) {
    clearLocalStorageExceptSimulationHistory();
    sessionStorage.clear();
    this.storage = localStorage;
    this.storage.setItem('rememberMe', remember);
    this.storage.setItem('active', true);
  },
  isAuthenticated() {
    if (!this.hasCurrentSession()) {
      if (this.getStorage().getItem('access_token')) this.signout(() => {});
      return false;
    }
    return Boolean(this.simulatorUserId()) && this.simulatorUserId() === this.tokenClaims().id;
  },
  authenticate(user, cb) {
    this.getStorage().setItem('userId', user.userId);
    this.getStorage().setItem('cuit', user.cuit);
    this.getStorage().setItem('reportTo', user.reportTo_Cuit);
    this.getStorage().setItem('userAvatar', user.avatar);
    this.getStorage().setItem('userName', user.userFirstName + ' ' + user.userLastName);
    this.getStorage().setItem('user', user.userName);
    const accountType = accountTypeOf(user);
    this.getStorage().setItem('accountType', String(accountType));
    this.getStorage().setItem('roles', JSON.stringify(roleNames(user)));
    this.getStorage().setItem('userEmailConfirmed', user.emailConfirmed);
    this.getStorage().setItem('userEmail', user.email);
    this.getStorage().setItem('userCode', user.code);
    this.getStorage().setItem('cantidadFacturasPendientes', user.sysData?.cantidadFacturasPendientes);
    this.getStorage().setItem('cantidadFacturasPendientesPermitidas', user.sysData?.cantidadFacturasPendientesPermitidas);
    this.getStorage().setItem('descripcionTipoDePago', user.sysData?.descripcionTipoDePago);
    this.getStorage().setItem('emails', user.sysData?.emails);
    cb();
  },
  // Datos que se setean cuando simulas un PAS
  setUserIdProducer(id, name, cuit, sysData, reportTo_Cuit, roles, registered, effectivePermissions = [], targetAccountType = 0) {
    this.getStorage().setItem('userIdProducer', id);
    this.getStorage().setItem('userNameProducer', name);
    this.getStorage().setItem('cuitProducer', cuit);
    const responsibleCuit = String(reportTo_Cuit ?? '').trim();
    this.getStorage().setItem('simulationEffectiveCuit', responsibleCuit && Number(responsibleCuit) > 0 ? responsibleCuit : String(cuit));
    const targetRoles = roleNames({ roles });
    this.getStorage().setItem('simulationTargetRoles', JSON.stringify(targetRoles));
    this.getStorage().setItem('simulationTargetAccountType', String(Number(targetAccountType) === 1 ? Number(targetAccountType) : 0));
    // Payment summaries are loaded by their module, never persisted with the simulated identity.
    this.clearGiccpData();
    this.getStorage().setItem('registered', registered);
    this.getStorage().setItem('simulatedPermissions', JSON.stringify(effectivePermissions));
    this.getStorage().setItem('simulationExpiresAt', String(this.tokenClaims().exp || 0));
    this.getStorage().setItem('simulationContextId', `${Date.now()}-${Math.random()}`);
  },
  isRegistered(){
    return this.isProducer() || this.getStorage().registered === 'true';
  },
  email() {
    return this.getStorage().userEmail;
  },
  cuit() {
    return this.getStorage().cuit;
  },
  userId() {
    let userId;
    if (this.requestSelectProducer() === false && !this.isUserSimulator()) {
      if (this.getStorage().userIdProducer)
        userId = this.getStorage().userIdProducer;
      else userId = this.getStorage().userId;
    }
    else {
      userId = this.getStorage().userId;
    }
    return userId;
  },
  simulatorUserId() {
    return this.getStorage().userId;
  },
  isGestor() {
    try {
      return this.accountType() === 1 && Number(this.reportTo()) > 0;
    } catch (error) {
      return false;
    }
  },
  // Business queries use the responsible producer. Actor and selected target identities remain separate.
  getProducerCuit() {
    if (this.userIdProducer() !== undefined) return this.cuitProducer();
    return this.isGestor() ? this.reportTo() : this.isProducer() ? this.cuit() : undefined;
  },
  getProducerId() {
    if (this.isSimulating()) return this.userIdProducer();
    return this.isGestor() ? this.reportTo() : this.isProducer() ? this.userId() : undefined;
  },
  isSimulating() { return this.userIdProducer() !== undefined; },
  businessAccountType() { return this.isSimulating() ? Number(this.getStorage().simulationTargetAccountType || 0) : Number(this.accountType()); },
  businessIsGestor() { return this.isSimulating() ? this.cuitProducer() !== this.selectedProducerCuit() : this.isGestor(); },
  businessRoles() {
    if (!this.isSimulating()) return this.roles();
    try { return JSON.parse(this.getStorage().simulationTargetRoles) || []; } catch { return []; }
  },
  reportTo() {
    return this.getStorage().reportTo;
  },
  accountType() {
    return this.hasCurrentSession() ? accountTypeOf(this.tokenClaims()) : 0;
  },
  tokenClaims() {
    try { return jwtDecode(this.getStorage().getItem('access_token')); } catch { return {}; }
  },
  hasCurrentSession() { return isCurrentSession(this.tokenClaims()); },
  permissions() {
    const claims = this.tokenClaims();
    if (!this.hasCurrentSession()) return [];
    const value = claims.permission;
    return [...new Set((Array.isArray(value) ? value : [value]).filter(value => typeof value === 'string'))];
  },
  businessPermissions() {
    if (!this.hasCurrentSession()) return [];
    if (this.userIdProducer() === undefined) return this.permissions();
    if (this.accountType() !== 2 || !this.hasPermission('simulator.use')) return [];
    try {
      if (Number(this.getStorage().getItem('simulationExpiresAt')) * 1000 <= Date.now()) return [];
      const permissions = JSON.parse(this.getStorage().getItem('simulatedPermissions'));
      const actorPermissions = new Set(this.permissions());
      return Array.isArray(permissions) ? permissions.filter(permission => actorPermissions.has(permission)) : [];
    } catch { return []; }
  },
  roles() {
    const claims = this.tokenClaims();
    const value = claims.role ?? claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    if (value) return Array.isArray(value) ? value : [value];
    return [];
  },
  hasPermission(permission) { return this.permissions().includes(permission); },
  hasAnyPermission(...permissions) { return permissions.some(permission => this.hasPermission(permission)); },
  hasBusinessPermission(permission) {
    if (!SCOPED_BUSINESS_PERMISSIONS.has(permission)) return false;
    return this.businessPermissions().includes(permission);
  },
  hasAnyBusinessPermission(...permissions) { return permissions.some(permission => this.hasBusinessPermission(permission)); },
  canReadDelegates() {
    return this.isRegistered() && this.businessAccountType() === 1 && !this.businessIsGestor() && this.hasAnyBusinessPermission('producers.delegates.read', 'producers.delegates.manage');
  },
  canManageDelegates() {
    return this.isRegistered() && this.businessAccountType() === 1 && !this.businessIsGestor() && this.hasBusinessPermission('producers.delegates.manage');
  },
  canEditProfile() { return !this.isSimulating(); },
  userAvatar() {
    return this.getStorage().userAvatar;
  },
  setUserAvatar(avatar) {
    this.getStorage().setItem('userAvatar', avatar);
  },
  userName() {
    return this.getStorage().userName;
  },
  setUserName(userName) {
    this.getStorage().setItem('userName', userName);
  },
  user() {
    return this.getStorage().user;
  },
  userIdProducer() {
    return this.getStorage().userIdProducer;
  },
  userNameProducer() {
    return this.getStorage().userNameProducer;
  },
  cuitProducer() {
    return this.getStorage().simulationEffectiveCuit ?? this.getStorage().cuitProducer;
  },
  selectedProducerCuit() {
    return this.getStorage().cuitProducer;
  },
  getAppVersion() {
    const storage = this.getStorage();
    return (storage != null) ? storage.appVersion : null;
  },
  setAppVersion(appVersion) {
    let storage = this.getStorage();
    if (storage != null) storage.setItem('appVersion', appVersion);
  },
  isProducer() {
    // Historical callers use this helper for the external portal, including gestores.
    return this.accountType() === 1;
  },
  isUserSimulator() {
    return this.accountType() === 2 && !this.isAdmin();
  },
  isAdmin() {
    return this.accountType() === 2 && this.hasAnyPermission('users.read', 'users.manage', 'roles.read', 'roles.manage', 'configurations.read', 'configurations.manage', 'logs.read', 'logs.manage', 'notifications.read', 'notifications.manage', 'communications.read', 'communications.manage', 'labels.manage', 'audit.read');
  },
  requestSelectProducer() {
    return this.isUserSimulator() === true && this.hasPermission('simulator.use') && this.cuitProducer() === undefined;
  },
  userCode() {
    return this.getStorage().userCode;
  },
  requestChangePassword() {
    return this.getStorage().userEmailConfirmed === "false" && this.isUserSimulator();
  },
  setUserEmailConfirmed(value) {
    this.getStorage().setItem('userEmailConfirmed', value);
  },
  getGiccpData() {
    return {
      descripcionTipoDePago: this.getStorage().descripcionTipoDePago,
      cantidadFacturasPendientesPermitidas: this.getStorage().cantidadFacturasPendientesPermitidas,
      cantidadFacturasPendientes: this.getStorage().cantidadFacturasPendientes
    }
  },
  clearGiccpData() {
    ['cantidadFacturasPendientes', 'cantidadFacturasPendientesPermitidas', 'descripcionTipoDePago', 'emails']
      .forEach(key => this.getStorage().removeItem(key));
  },
  cleanSimulator() {
    this.clearGiccpData();
    this.getStorage().removeItem('userIdProducer');
    this.getStorage().removeItem('userNameProducer');
    this.getStorage().removeItem('cuitProducer');
    this.getStorage().removeItem('simulationEffectiveCuit');
    this.getStorage().removeItem('simulationTargetRoles');
    this.getStorage().removeItem('simulationTargetAccountType');
    this.getStorage().removeItem('registered');
    this.getStorage().removeItem('menuAdvanced');
    this.getStorage().removeItem('menu-selected');
    this.getStorage().removeItem('simulatedPermissions');
    this.getStorage().removeItem('simulationExpiresAt');
    this.getStorage().removeItem('simulationContextId');
  },
  remember() {
    return this.getStorage().userId !== undefined && this.getStorage().rememberMe;
  },
  signout(cb) {
    queryClient.clear();
    sessionStorage.clear();
    clearLocalStorageExceptSimulationHistory();
    cb();
  }
};

export default userAuth;
